import React, { useState, useEffect } from 'react';
import { updateCategoria, getCategoriasFlat, Categoria } from '../services/api';
import { getHierarchicalCategories } from '../utils/categoryUtils';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Alert
} from '@mui/material';

interface CategoriaEditModalProps {
    categoria: Categoria | null;
    open: boolean;
    onClose: () => void;
    onCategoriaUpdated: () => void;
}

const CategoriaEditModal: React.FC<CategoriaEditModalProps> = ({ categoria, open, onClose, onCategoriaUpdated }) => {
    const [nome, setNome] = useState('');
    const [categoriaPaiId, setCategoriaPaiId] = useState<number | string>('');
    const [parentCategories, setParentCategories] = useState<Categoria[]>([]);
    const [error, setError] = useState<string>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchParentCategories = async () => {
            try {
                const flatCategorias = await getCategoriasFlat();
                setParentCategories(flatCategorias);
            } catch (err) {
                console.error("Erro ao buscar categorias pai:", err);
            }
        };
        if (open) {
            fetchParentCategories();
            setError('');
        }
    }, [open]);

    useEffect(() => {
        if (categoria) {
            setNome(categoria.nome);
            setCategoriaPaiId(categoria.categoria_pai_id || '');
        } else {
            setNome('');
            setCategoriaPaiId('');
        }
        setError('');
    }, [categoria, open]);

    const hasSubcategories = Boolean(
        categoria && (
            (categoria.subcategorias && categoria.subcategorias.length > 0) ||
            parentCategories.some(c => c.categoria_pai_id === categoria.id)
        )
    );

    const handleSave = async () => {
        if (!categoria) return;
        if (!nome.trim()) {
            setError('O nome da categoria é obrigatório.');
            return;
        }

        const updatedCategoria = {
            nome: nome.trim(),
            categoria_pai_id: categoriaPaiId ? Number(categoriaPaiId) : null
        };

        setSaving(true);
        setError('');
        try {
            await updateCategoria(categoria.id, updatedCategoria);
            onCategoriaUpdated();
            onClose();
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Erro ao atualizar categoria.');
        } finally {
            setSaving(false);
        }
    };

    const hierarchicalOptions = getHierarchicalCategories(parentCategories).filter(
        cat => cat.id !== categoria?.id
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Editar Categoria</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                    label="Nome da Categoria"
                    value={nome}
                    onChange={e => setNome(e.target.value)}
                    required
                    sx={{ mt: 1 }}
                />
                <FormControl fullWidth disabled={hasSubcategories}>
                    <InputLabel id="categoria-pai-label">Categoria Pai (Opcional)</InputLabel>
                    <Select
                        labelId="categoria-pai-label"
                        value={categoriaPaiId}
                        label="Categoria Pai (Opcional)"
                        onChange={e => setCategoriaPaiId(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>Nenhuma (Categoria Principal)</em>
                        </MenuItem>
                        {hierarchicalOptions.map((cat) => (
                            <MenuItem
                                key={cat.id}
                                value={cat.id}
                                disabled={cat.level > 0}
                                sx={{ pl: (cat.level * 2) + 2 }}
                            >
                                {cat.level > 0 ? '— '.repeat(cat.level) : ''}{cat.nome}
                            </MenuItem>
                        ))}
                    </Select>
                    {hasSubcategories && (
                        <FormHelperText>
                            Esta categoria possui subcategorias e não pode ser transformada em subcategoria de outra.
                        </FormHelperText>
                    )}
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CategoriaEditModal;
